import type { ContentBounds } from './clampPan.tsx';
import { STATUS_HEIGHT, START_NODE_RADIUS } from './ellipsis.tsx';
import { getCanvasBoundingBox, expandBoundingBox } from './getCanvasBoundingBox.tsx';
import { CONTAINER_WIDTH, getContainerHeight } from './getLozengePosition.tsx';
import type { Point } from './toPortInfo.tsx';

export const DEFAULT_STATUS_WIDTH = 100;

type BoundingRect = {
	x: number;
	y: number;
	width: number;
	height: number;
};

export type CalculateContentBoundsParams = {
	statuses: Array<{ id: string; position?: Point }>;
	statusWidths?: Record<string, number>;
	startNodePosition?: Point;
	globalLoopsContainerPosition?: Point;
	globalLoopedTransitionsCount?: number;
	padding?: number;
	defaultPositions?: Record<string, Point>;
};

export const calculateContentBounds = ({
	statuses,
	statusWidths = {},
	startNodePosition,
	globalLoopsContainerPosition,
	globalLoopedTransitionsCount = 0,
	padding = 100,
	defaultPositions,
}: CalculateContentBoundsParams): ContentBounds | undefined => {
	const bounds: BoundingRect[] = [];

	for (const status of statuses) {
		const position = status.position ?? defaultPositions?.[status.id];
		if (position) {
			const width = statusWidths[status.id] ?? DEFAULT_STATUS_WIDTH;
			bounds.push({
				x: position.x,
				y: position.y,
				width,
				height: STATUS_HEIGHT,
			});
		}
	}

	if (startNodePosition) {
		bounds.push({
			x: startNodePosition.x - START_NODE_RADIUS,
			y: startNodePosition.y - START_NODE_RADIUS,
			width: START_NODE_RADIUS * 2,
			height: START_NODE_RADIUS * 2,
		});
	}

	if (globalLoopsContainerPosition && globalLoopedTransitionsCount > 0) {
		bounds.push({
			x: globalLoopsContainerPosition.x,
			y: globalLoopsContainerPosition.y,
			width: CONTAINER_WIDTH,
			height: getContainerHeight(globalLoopedTransitionsCount),
		});
	}

	if (bounds.length === 0) {
		return undefined;
	}

	const contentBounds = getCanvasBoundingBox(
		bounds.map((b) => new DOMRect(b.x, b.y, b.width, b.height)),
	);

	return contentBounds ? expandBoundingBox(contentBounds, padding) : undefined;
};
